import { useState, useEffect, useCallback } from 'react';
import { cpApi } from '../api/api';
import { useTransactionsFilters } from '../context/TransactionsFilterContext';
import type { Transaction } from '../types';

const formatDate = (s: string) => {
  try {
    const d = new Date(s);
    return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return '-';
  }
};

const formatNum = (v: number | null | undefined) => {
  if (v == null) return '-';
  return new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
};

const getTypeDisplay = (tx: Transaction) => {
  const type = (tx.transaction_type || '').toLowerCase();
  if (type.includes('withdraw')) return { text: 'Изъятие', bg: 'bg-red-200 dark:bg-red-900/30', textColor: 'text-red-900 dark:text-red-200' };
  if (type.includes('deposit')) return { text: 'Пополнение', bg: 'bg-green-200 dark:bg-green-900/30', textColor: 'text-green-900 dark:text-green-200' };
  if (type.includes('counterparty') || type.includes('transfer') || type.includes('wallet_to_counterparty')) {
    return { text: 'Перевод', bg: 'bg-indigo-200 dark:bg-indigo-900/30', textColor: 'text-indigo-900 dark:text-indigo-200' };
  }
  if (tx.article?.includes('Прибыль с мэтча')) return { text: 'Заявка', bg: 'bg-blue-200 dark:bg-blue-900/30', textColor: 'text-blue-900 dark:text-blue-200' };
  return { text: 'Операция', bg: 'bg-gray-200 dark:bg-gray-700/60', textColor: 'text-gray-900 dark:text-gray-100' };
};

export default function TransactionHistoryPage() {
  const txFilters = useTransactionsFilters();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const limit = 20;

  const search = txFilters?.filters.search ?? '';
  const memberFilter = txFilters?.filters.memberFilter ?? 'all';
  const typeFilter = txFilters?.filters.typeFilter ?? 'all';
  const dateRange = txFilters?.filters.dateRange ?? { from: null, to: null };
  const dateFrom = dateRange.from ? `${dateRange.from.getFullYear()}-${String(dateRange.from.getMonth() + 1).padStart(2, '0')}-${String(dateRange.from.getDate()).padStart(2, '0')}` : '';
  const dateTo = dateRange.to ? `${dateRange.to.getFullYear()}-${String(dateRange.to.getMonth() + 1).padStart(2, '0')}-${String(dateRange.to.getDate()).padStart(2, '0')}` : '';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const fetchLimit = search.trim() ? 200 : limit;
      const fetchOffset = search.trim() ? 0 : (page - 1) * limit;
      const txRes = await cpApi.getTeamTransactions({
          limit: fetchLimit,
          offset: fetchOffset,
          type: typeFilter !== 'all' ? typeFilter : undefined,
          member_id: memberFilter !== 'all' ? parseInt(memberFilter, 10) : undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
        });
      let list = txRes.transactions ?? [];
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        list = list.filter(
          (tx: Transaction) =>
            String(tx.id).includes(q) ||
            (tx.to_entity_name ?? '').toLowerCase().includes(q) ||
            (tx.article ?? '').toLowerCase().includes(q) ||
            (tx.comment ?? '').toLowerCase().includes(q)
        );
      }
      setTransactions(list);
      setTotal(search.trim() ? list.length : (txRes.total ?? 0));
    } catch {
      setTransactions([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, memberFilter, typeFilter, dateFrom, dateTo, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [memberFilter, typeFilter, dateRange.from, dateRange.to]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener('refreshTransactions', h);
    return () => window.removeEventListener('refreshTransactions', h);
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="px-6 pb-2 pt-2 -mx-4">
      <div className="relative">
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <p className="text-gray-500 dark:text-gray-300 text-lg font-medium">Нет транзакций</p>
              <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">Транзакции появятся здесь после их создания</p>
            </div>
          ) : (
            <>
            <div className="md:hidden p-2 space-y-2">
              {transactions.map((tx) => {
                const typeInfo = getTypeDisplay(tx);
                return (
                  <div key={`mobile-tx-${tx.id}`} className="rounded-xl border border-gray-200 dark:border-gray-700/60 bg-white/80 dark:bg-white/5 p-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold text-gray-900 dark:text-gray-100">#{tx.id}</span>
                      <span className={`inline-flex px-2 py-0.5 rounded text-[11px] font-medium ${typeInfo.bg} ${typeInfo.textColor}`}>{typeInfo.text}</span>
                    </div>
                    <div className="mt-1 text-sm font-semibold text-gray-900 dark:text-gray-100">{formatNum(tx.amount)}</div>
                    <div className="mt-0.5 text-[11px] text-gray-600 dark:text-gray-300 truncate">{tx.to_entity_name ?? `ID ${tx.to_entity_id}`}</div>
                    <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                      До: {formatNum(tx.to_calc_balance_before)} · После: {formatNum(tx.to_calc_balance_after)}
                    </div>
                    <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">{formatDate(tx.created_at)}</div>
                    {tx.comment && <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400 truncate" title={tx.comment}>{tx.comment}</div>}
                  </div>
                );
              })}
            </div>
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/30 dark:to-blue-900/30">
                  <tr>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">ID</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider border-l border-gray-100/60 dark:border-gray-700/60">Участник</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider border-l border-gray-100/60 dark:border-gray-700/60">Сумма</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider border-l border-gray-100/60 dark:border-gray-700/60">Баланс до</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider border-l border-gray-100/60 dark:border-gray-700/60">Баланс после</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider border-l border-gray-100/60 dark:border-gray-700/60">Тип</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider border-l border-gray-100/60 dark:border-gray-700/60">Коммент</th>
                    <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider border-l border-gray-100/60 dark:border-gray-700/60">Дата</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100/60 dark:divide-gray-700/60">
                  {transactions.map((tx, idx) => {
                    const typeInfo = getTypeDisplay(tx);
                    return (
                      <tr
                        key={tx.id}
                        className={`${idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50/40 dark:bg-gray-900/20'} hover:bg-purple-50/40 dark:hover:bg-purple-900/10 transition-colors`}
                      >
                        <td className="px-2 py-2 text-left whitespace-nowrap text-xs text-gray-900 dark:text-gray-100 tabular-nums">{tx.id}</td>
                        <td className="px-2 py-2 text-left whitespace-nowrap text-xs text-gray-900 dark:text-gray-100 border-l border-gray-100/60 dark:border-gray-700/60">{tx.to_entity_name ?? `ID ${tx.to_entity_id}`}</td>
                        <td className="px-2 py-2 text-left whitespace-nowrap text-xs font-semibold text-gray-900 dark:text-gray-100 tabular-nums border-l border-gray-100/60 dark:border-gray-700/60">{formatNum(tx.amount)}</td>
                        <td className="px-2 py-2 text-left whitespace-nowrap text-xs text-gray-700 dark:text-gray-200 tabular-nums border-l border-gray-100/60 dark:border-gray-700/60">{formatNum(tx.to_calc_balance_before)}</td>
                        <td className="px-2 py-2 text-left whitespace-nowrap text-xs text-gray-700 dark:text-gray-200 tabular-nums border-l border-gray-100/60 dark:border-gray-700/60">{formatNum(tx.to_calc_balance_after)}</td>
                        <td className="px-2 py-2 text-left whitespace-nowrap border-l border-gray-100/60 dark:border-gray-700/60">
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${typeInfo.bg} ${typeInfo.textColor}`}>{typeInfo.text}</span>
                        </td>
                        <td className="px-2 py-2 text-left text-xs text-gray-500 dark:text-gray-400 max-w-xs truncate border-l border-gray-100/60 dark:border-gray-700/60" title={tx.comment ?? undefined}>{tx.comment || '-'}</td>
                        <td className="px-2 py-2 text-left whitespace-nowrap text-xs text-gray-600 dark:text-gray-400 border-l border-gray-100/60 dark:border-gray-700/60">{formatDate(tx.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}

          {totalPages > 1 && transactions.length > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Страница {page} из {totalPages}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1 text-sm rounded-lg border border-gray-200 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  ←
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-3 py-1 text-sm rounded-lg border border-gray-200 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
